import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, User, MessageSquare, Loader2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { sendContactMessage } from "../store/contact/contactActions";
import { Formik } from "formik";
import * as Yup from "yup";

// ✅ Validation Schema
const contactValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be 50 characters or less")
    .required("Name is required"),

  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),

  message: Yup.string()
    .trim()
    .min(10, "Message must be at least 10 characters")
    .max(1000, "Message must be 1000 characters or less")
    .required("Message is required")
});

const ContactUsPage = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.contact);

  // ✅ Form Submit Handler
  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await dispatch(sendContactMessage(values)).unwrap();
      resetForm();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    name: "",
    email: "",
    message: ""
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={contactValidationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur
      validateOnChange
    >
      {({
        isSubmitting,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        values
      }) => (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto max-w-2xl py-8 sm:py-12 px-4"
        >
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-primary via-purple-500 to-accent text-transparent bg-clip-text mb-4">
              Get in Touch
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground">
              Have questions or feedback? We'd love to hear from you!
            </p>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-8 bg-card p-4 sm:p-8 rounded-xl shadow-2xl border border-border/20"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {/* 🧍 Name Field */}
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-sm font-medium text-foreground/80 flex items-center"
              >
                <User className="w-4 h-4 mr-2 text-primary" /> Full Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="e.g. Alex Johnson"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`bg-background/50 border-border/30 focus:border-primary ${
                  errors.name && touched.name ? "border-red-400" : ""
                }`}
              />
              <AnimatePresence>
                {errors.name && touched.name && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-red-400"
                  >
                    {errors.name}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* 📧 Email Field */}
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-medium text-foreground/80 flex items-center"
              >
                <Mail className="w-4 h-4 mr-2 text-primary" /> Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="e.g. alex.johnson@example.com"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`bg-background/50 border-border/30 focus:border-primary ${
                  errors.email && touched.email ? "border-red-400" : ""
                }`}
              />
              <AnimatePresence>
                {errors.email && touched.email && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-red-400"
                  >
                    {errors.email}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* 💬 Message Field */}
            <div className="space-y-2">
              <Label
                htmlFor="message"
                className="text-sm font-medium text-foreground/80 flex items-center"
              >
                <MessageSquare className="w-4 h-4 mr-2 text-primary" /> Your
                Message
              </Label>
              <Textarea
                id="message"
                name="message"
                placeholder="Tell us how we can help..."
                value={values.message}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={6}
                className={`bg-background/50 border-border/30 focus:border-primary ${
                  errors.message && touched.message ? "border-red-400" : ""
                }`}
              />
              <AnimatePresence>
                {errors.message && touched.message && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-red-400"
                  >
                    {errors.message}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* 🚀 Submit Button */}
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 text-base transition-all duration-300 ease-in-out transform hover:scale-105"
              disabled={isSubmitting || loading}
            >
              {(isSubmitting || loading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Send Message
            </Button>
          </motion.form>
        </motion.div>
      )}
    </Formik>
  );
};

export default ContactUsPage;
